import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';

import {
  InsuranceHistoryRow,
  InsuranceState,
  InsuranceStatusPayload,
  InsuranceTrigger,
} from '../../../../../core/df/df.types';
import { StatusChipComponent } from '../../../../../shared/status-chip/status-chip';
import { DashPipe } from '../../../../../shared/pipes/dash.pipe';
import {
  InsuranceStatusDialogComponent,
  InsuranceStatusDialogData,
} from './insurance-status-dialog';

/**
 * `§C7` — the business's insurance status on the Debtors Finance facility page, with
 * the control to change it and the audit trail beneath. Ops-only (N4). The panel
 * emits nothing: the dialog calls `save`, which the page binds to its service and
 * its facility signal.
 */
@Component({
  selector: 'app-insurance-panel',
  imports: [DatePipe, DashPipe, MatButtonModule, StatusChipComponent],
  template: `
    <div class="insurance">
      <div class="insurance__head">
        @if (status(); as s) {
          <app-status-chip [status]="s" />
        } @else {
          <span>Not recorded</span>
        }
        @if (status() === 'WITHDRAWN') {
          <span class="insurance__note">Permanent: a new application is required.</span>
        } @else {
          <button mat-stroked-button type="button" (click)="open()" data-testid="ins-open">
            {{ status() ? 'Change insurance status' : 'Record insurance as active' }}
          </button>
        }
      </div>
      @if (!status()) {
        <p class="insurance__note">
          Approval is blocked until a person records insurance as active.
        </p>
      }
      @if (history().length > 0) {
        <ul class="insurance__history" data-testid="ins-history">
          @for (h of history(); track h.created_at) {
            <li>
              <strong>{{ h.from_state ? h.from_state + ' to ' : 'Set to ' }}{{ h.to_state }}</strong>
              {{ h.trigger | dash }}, {{ h.by | dash }},
              {{ h.created_at | date: 'medium' }}
              @if (h.cc_ref) {
                , Credit Committee {{ h.cc_ref }}
              }
              <div class="insurance__reason">{{ h.reason }}</div>
            </li>
          }
        </ul>
      }
    </div>
  `,
  styles: `
    .insurance__head {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      flex-wrap: wrap;
    }
    .insurance__note {
      color: var(--text-secondary);
      font-size: 0.875rem;
    }
    .insurance__history {
      margin: var(--space-3) 0 0;
      padding-left: var(--space-4);
      font-size: 0.875rem;
    }
    .insurance__reason {
      color: var(--text-secondary);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsurancePanelComponent {
  private readonly dialog = inject(MatDialog);

  readonly status = input<InsuranceState | null>(null);
  readonly history = input<InsuranceHistoryRow[]>([]);
  readonly triggers = input.required<Record<'SUSPENDED' | 'WITHDRAWN', InsuranceTrigger[]>>();
  readonly save = input.required<(p: InsuranceStatusPayload) => Observable<unknown>>();

  protected open(): void {
    const data: InsuranceStatusDialogData = {
      current: this.status(),
      triggers: this.triggers(),
      save: this.save(),
    };
    this.dialog.open(InsuranceStatusDialogComponent, { data });
  }
}
